import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob } from '@/utils/download';
import { DEFAULT_MP_PAGE_SIZE } from '../constants/marketPromotion.constants';
import {
  approvalTotalListSchema,
  donationBatchMemberListSchema,
  donationBatchResponseSchema,
  donationBudgetResponseSchema,
  donationCandidateLineListSchema,
  donationCandidateListSchema,
  donationProjectListSchema,
  donationRequisitionResponseSchema,
  requisitionInstituteListSchema,
  marketProjectResponseSchema,
  type ApprovalReportSearch,
  type ApprovalTotal,
  type DonationBatch,
  type DonationBatchMember,
  type DonationBatchResponse,
  type DonationBatchSearch,
  type DonationBudgetPayload,
  type DonationBudgetResponse,
  type DonationBudgetSearch,
  type DonationCandidate,
  type DonationCandidateLine,
  type DonationCandidateParams,
  type UpdateDonationBudgetPayload,
  type ZonalManagerDonationParams,
  type DonationProject,
  type DonationRequisitionPayload,
  type DonationRequisitionResponse,
  type DonationRequisitionSearch,
  type RequisitionInstitute,
  type MarketProjectPayload,
  type MarketProjectResponse,
  type MarketProjectSearch,
  type PublishDonationPayload,
  type UpdateMarketProjectPayload,
} from '../types/marketPromotionP2.types';

const ISO_DATE_LENGTH = 10;
const stamp = (): string => new Date().toISOString().slice(0, ISO_DATE_LENGTH);

export const mpP2QueryKeys = {
  all: () => ['market-promotion-p2'] as const,
  projects: (params: MarketProjectSearch) => [...mpP2QueryKeys.all(), 'projects', params] as const,
  budgets: (params: DonationBudgetSearch) => [...mpP2QueryKeys.all(), 'budgets', params] as const,
  batches: (stage: string, params: DonationBatchSearch) =>
    [...mpP2QueryKeys.all(), 'batches', stage, params] as const,
  batchMembers: (batchId: number) => [...mpP2QueryKeys.all(), 'batch-members', batchId] as const,
  batch: (batchId: number) => [...mpP2QueryKeys.all(), 'batch', batchId] as const,
  candidates: (params: DonationCandidateParams) =>
    [...mpP2QueryKeys.all(), 'candidates', params] as const,
  candidateLines: (params: ZonalManagerDonationParams) =>
    [...mpP2QueryKeys.all(), 'candidate-lines', params] as const,
  donationProjects: () => [...mpP2QueryKeys.all(), 'donation-projects'] as const,
  requisitions: (params: DonationRequisitionSearch) =>
    [...mpP2QueryKeys.all(), 'requisitions', params] as const,
  requisitionInstitutes: (thanaId: number | undefined, search: string) =>
    [...mpP2QueryKeys.all(), 'requisition-institutes', thanaId, search] as const,
  totals: (params: ApprovalReportSearch) => [...mpP2QueryKeys.all(), 'totals', params] as const,
};

export const marketProjectsInfiniteQueryOptions = (params: MarketProjectSearch) =>
  infiniteQueryOptions({
    queryKey: mpP2QueryKeys.projects(params),
    queryFn: async ({ pageParam }): Promise<MarketProjectResponse> => {
      const res = await apiClient.get(API.marketPromotion.projects, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return marketProjectResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function createMarketProject(payload: MarketProjectPayload): Promise<void> {
  await apiClient.post(API.marketPromotion.projects, payload);
}

export async function updateMarketProject(
  id: number,
  payload: UpdateMarketProjectPayload,
): Promise<void> {
  await apiClient.put(API.marketPromotion.project(id), payload);
}

export const donationBudgetsInfiniteQueryOptions = (params: DonationBudgetSearch) =>
  infiniteQueryOptions({
    queryKey: mpP2QueryKeys.budgets(params),
    queryFn: async ({ pageParam }): Promise<DonationBudgetResponse> => {
      const res = await apiClient.get(API.marketPromotion.budgets, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return donationBudgetResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function createDonationBudget(payload: DonationBudgetPayload): Promise<void> {
  await apiClient.post(API.marketPromotion.budgets, payload);
}

export async function updateDonationBudget(
  id: number,
  payload: UpdateDonationBudgetPayload,
): Promise<void> {
  await apiClient.put(API.marketPromotion.budget(id), payload);
}

export const donationBatchesInfiniteQueryOptions = (stage: string, params: DonationBatchSearch) =>
  infiniteQueryOptions({
    queryKey: mpP2QueryKeys.batches(stage, params),
    queryFn: async ({ pageParam }): Promise<DonationBatchResponse> => {
      const res = await apiClient.get(API.marketPromotion.donationBatches, {
        params: { ...params, stage, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return donationBatchResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const donationMembersQueryOptions = (batchId: number) =>
  queryOptions({
    queryKey: mpP2QueryKeys.batchMembers(batchId),
    queryFn: async (): Promise<DonationBatchMember[]> => {
      const res = await apiClient.get(API.marketPromotion.donationBatchMembers(batchId));
      return donationBatchMemberListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const donationBatchQueryOptions = (batchId: number) =>
  queryOptions({
    queryKey: mpP2QueryKeys.batch(batchId),
    queryFn: async (): Promise<DonationBatch | null> => {
      const res = await apiClient.get(API.marketPromotion.donationBatches, {
        params: { batchId, page: FIRST_PAGE, pageSize: 1 },
      });
      return donationBatchResponseSchema.parse(res.data).data[0] ?? null;
    },
    staleTime: STALE_TIME,
  });

export const donationCandidatesQueryOptions = (params: DonationCandidateParams) =>
  queryOptions({
    queryKey: mpP2QueryKeys.candidates(params),
    queryFn: async (): Promise<DonationCandidate[]> => {
      const res = await apiClient.get(API.marketPromotion.donationCandidates, { params });
      return donationCandidateListSchema.parse(res.data);
    },
    enabled: params.promoItemId !== undefined,
    staleTime: STALE_TIME,
  });

export const donationCandidateLinesQueryOptions = (params: ZonalManagerDonationParams) =>
  queryOptions({
    queryKey: mpP2QueryKeys.candidateLines(params),
    queryFn: async (): Promise<DonationCandidateLine[]> => {
      const res = await apiClient.get(API.marketPromotion.donationCandidateLines, { params });
      return donationCandidateLineListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const zonalManagerCandidateQueryOptions = (params: ZonalManagerDonationParams) =>
  queryOptions({
    queryKey: mpP2QueryKeys.candidates(params),
    queryFn: async (): Promise<DonationCandidate | null> => {
      const res = await apiClient.get(API.marketPromotion.donationCandidates, { params });
      return donationCandidateListSchema.parse(res.data)[0] ?? null;
    },
    staleTime: STALE_TIME,
  });

export const donationProjectsQueryOptions = () =>
  queryOptions({
    queryKey: mpP2QueryKeys.donationProjects(),
    queryFn: async (): Promise<DonationProject[]> => {
      const res = await apiClient.get(API.marketPromotion.donationProjects);
      return donationProjectListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function publishDonationBatch(payload: PublishDonationPayload): Promise<void> {
  await apiClient.post(API.marketPromotion.donationBatches, payload);
}

export async function confirmDonationBatch(
  batchId: number,
  isConfirm: boolean,
  comment?: string,
): Promise<void> {
  await apiClient.patch(API.marketPromotion.confirmDonationBatch(batchId), {
    isConfirm,
    comment,
  });
}

export async function disburseDonationBatch(batchId: number, comment: string): Promise<void> {
  await apiClient.patch(API.marketPromotion.disburseDonationBatch(batchId), { comment });
}

export async function exportDonationMembers(batchId: number): Promise<void> {
  const res = await apiClient.get(API.marketPromotion.donationBatchMembersExport(batchId), {
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `${batchId}-DonationPublishedList.xlsx`);
}

export const donationRequisitionsInfiniteQueryOptions = (params: DonationRequisitionSearch) =>
  infiniteQueryOptions({
    queryKey: mpP2QueryKeys.requisitions(params),
    queryFn: async ({ pageParam }): Promise<DonationRequisitionResponse> => {
      const res = await apiClient.get(API.marketPromotion.requisitions, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return donationRequisitionResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function createDonationRequisition(
  payload: DonationRequisitionPayload,
): Promise<void> {
  await apiClient.post(API.marketPromotion.requisitions, payload);
}

export async function updateDonationRequisition(
  id: number,
  payload: DonationRequisitionPayload,
): Promise<void> {
  await apiClient.put(API.marketPromotion.requisition(id), payload);
}

export const requisitionInstitutesQueryOptions = (thanaId: number | undefined, search: string) =>
  queryOptions({
    queryKey: mpP2QueryKeys.requisitionInstitutes(thanaId, search),
    queryFn: async (): Promise<RequisitionInstitute[]> => {
      const res = await apiClient.get(API.marketPromotion.requisitionInstitutes, {
        params: { thanaId, search: search === '' ? undefined : search },
      });
      return requisitionInstituteListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const approvalTotalsQueryOptions = (params: ApprovalReportSearch) =>
  queryOptions({
    queryKey: mpP2QueryKeys.totals(params),
    queryFn: async (): Promise<ApprovalTotal[]> => {
      const res = await apiClient.get(API.marketPromotion.approvalTotals, { params });
      return approvalTotalListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function exportCashCollection(
  params: ApprovalReportSearch,
  fileNamePrefix: string,
): Promise<void> {
  const res = await apiClient.get(API.marketPromotion.cashCollectionExport, {
    params,
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `${fileNamePrefix}-${stamp()}.xlsx`);
}
